/**
 * @flow
 */

import { apiBase } from "../const/apiConstants";
import { fetchAllPagesAndParseResponses, fetchWithAuthAndParseResponse, ResponseError } from "../fetchWithAuth";
import { parseRawOrgNodeArray, parseRawSimpleOrgNodeArray } from "../models/orgNode";
import type { OrgNode, RawOrgNode, RawSimpleOrgNode, SimpleOrgNode } from "../models/orgNode";

type SuggestOrgNodeAPIResponseData = Array<RawSimpleOrgNode>;

export const parseSuggestOrgNodeAPIResponseData = (data: SuggestOrgNodeAPIResponseData): Array<SimpleOrgNode> => {
  return parseRawSimpleOrgNodeArray(data);
};

export const requestSuggestOrgNode = (searchString: string): Promise<Array<SimpleOrgNode> | ResponseError> => {
  const searchParams = new URLSearchParams({
    search_term: searchString,
    page_size: "8",
  });

  return fetchWithAuthAndParseResponse(
    `${window.location.origin}${apiBase}common/business-structure-reference/suggest/?${searchParams.toString()}`,
    parseSuggestOrgNodeAPIResponseData
  );
};

export const requestSuggestJobOrgNode = (searchString: string): Promise<Array<SimpleOrgNode> | ResponseError> => {
  const searchParams = new URLSearchParams({
    search_term: searchString,
    page_size: "8",
    jobs_only: "true",
  });

  return fetchWithAuthAndParseResponse(
    `${window.location.origin}${apiBase}common/business-structure-reference/suggest/?${searchParams.toString()}`,
    parseSuggestOrgNodeAPIResponseData
  );
};

type PaginationMeta = {
  previous_link: ?string,
  next_link: ?string,
  number: number,
  paginator: {
    num_pages: number,
  },
};

export type OrgNodeForUserAPIResponseData = {
  results: Array<RawOrgNode>,
  meta: PaginationMeta,
};

type OrgNodeForUserParsedResponseData = {
  results: Array<OrgNode>,
  meta: PaginationMeta,
};

export type OrgNodeForUserSearchAPIResponseData = {
  related_nodes: Array<RawOrgNode>,
  results: Array<RawOrgNode>,
  meta: PaginationMeta,
};

type OrgNodeForUserSearchParsedResponseData = {
  relatedNodes: Array<OrgNode>,
  results: Array<OrgNode>,
  meta: PaginationMeta,
};

export const parseOrgNodeSearchAPIResponseData = (
  data: OrgNodeForUserSearchAPIResponseData
): OrgNodeForUserSearchParsedResponseData => {
  return {
    results: parseRawOrgNodeArray(data.results),
    relatedNodes: parseRawOrgNodeArray(data.related_nodes),
    meta: data.meta,
  };
};

export const parseOrgNodeForUserAPIResponseData = (
  data: OrgNodeForUserAPIResponseData
): OrgNodeForUserParsedResponseData => {
  return { results: parseRawOrgNodeArray(data.results), meta: data.meta };
};

/** A method for retrieving a subset of accessible org nodes on behalf of a user.
 * @param childrenOfReferenceID the reference ID to retrieve direct children of. Pass in 'initial' to retrieve the
 *  top node of each subtree a user has access to.
 * @param targetEmployeeID the user to retrieve nodes on behalf of. The requesting user must have permission to
 *  manage this user, or they'll receive a permission denied response.
 * @param isRequestingLocationPreferences if set to true, org node requests return the org nodes the user has access to for their location based preferences.
 * @returns {Promise<ResponseError|Array<OrgNode>>}
 */
export const requestOrgNodesForUser = async (
  childrenOfReferenceID: string,
  targetEmployeeID?: ?string,
  isRequestingLocationPreferences: ?boolean
): Promise<OrgNodeForUserParsedResponseData | ResponseError> => {
  const searchParams = new URLSearchParams({
    // Copied from the page size of submittablesForEmployeeRawData to allow users with more org nodes to see more nodes.
    per_page: "300",
  });
  if (childrenOfReferenceID !== "initial") {
    searchParams.append("children_of_reference_id", childrenOfReferenceID);
  }
  if (targetEmployeeID != null) {
    searchParams.append("target_employee_id", targetEmployeeID);
  }
  // eslint-disable-next-line i18next/no-literal-string
  let requestURL = `${window.location.origin}${apiBase}common/business-structure-reference/`;
  if (isRequestingLocationPreferences) {
    // eslint-disable-next-line i18next/no-literal-string
    requestURL = `${window.location.origin}${apiBase}common/business-structure-reference/employee-preferences/`;
  }
  try {
    const response = await fetchAllPagesAndParseResponses<OrgNodeForUserAPIResponseData>(
      `${requestURL}?${searchParams.toString()}`,
      {},
      (meta: PaginationMeta) => meta.paginator.num_pages
    );
    const parsedResponses = response.map((r) => parseRawOrgNodeArray(r.data.results));
    return {
      results: parsedResponses.flatMap((d) => d),
      meta: response[response.length - 1].data.meta,
    };
  } catch (e) {
    return new ResponseError({
      code: e.code || -1,
      message: window.gettext("An error occurred while fetching available locations."),
    });
  }
};

export const requestSearchOrgNodesForUser = (
  searchTerm: string,
  targetEmployeeID?: ?string,
  isRequestingLocationPreferences?: ?boolean
): Promise<OrgNodeForUserSearchParsedResponseData | ResponseError> => {
  const searchParams = new URLSearchParams({
    search_term: searchTerm,
    per_page: "100", // since we need to get (potentially) lots of ancestry data, keep this value fairly small
  });
  if (targetEmployeeID != null) {
    searchParams.append("target_employee_id", targetEmployeeID);
  }
  // eslint-disable-next-line i18next/no-literal-string
  let requestURL = `${window.location.origin}${apiBase}common/business-structure-reference/search/`;
  if (isRequestingLocationPreferences) {
    // eslint-disable-next-line i18next/no-literal-string
    requestURL = `${window.location.origin}${apiBase}common/business-structure-reference/search-employee-preferences/`;
  }
  return fetchWithAuthAndParseResponse(`${requestURL}?${searchParams.toString()}`, parseOrgNodeSearchAPIResponseData);
};
